import React, { useState, useContext } from "react";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import moment from "moment";
import cleanseObj from "utils/cleanse-object";
import { DayPickerRangeController } from "react-dates";
import { MobileContext } from "./wrappers/withMobile";
import { BreakpointContext } from "./wrappers/withBreakpoint";
import InputContainer from "./ui/InputContainer";
import Text from "./ui/Text";
import DateField from "./DateField";
import "react-dates/lib/css/_datepicker.css";
import "react-dates/initialize";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file

import calendarIcon from "assets/calendar_pay.svg";

const errorMessages = {
  invalidDate: "The date you entered is invalid.",
  incompleteDate: "The date you entered is incomplete.",
  invalidTimeRange: "The time range you entered is invalid."
};

const convertToArray = errors =>
  Object.keys(errors)
    .map(error => ({
      type: error,
      message: errorMessages[error]
    }))
    .filter(error => error);

const Calendar = ({
  defaultFromDate,
  defaultToDate,
  allowableRange,
  onCalendarChange,
  onCalendarError
}) => {
  const { isMobile } = useContext(MobileContext);
  const { isBelow } = useContext(BreakpointContext);
  const [selected, setSelected] = useState(null);
  const [fromDate, setFromDate] = useState();
  const [toDate, setToDate] = useState();
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarFocus, setCalendarFocus] = useState("startDate");
  const [toErrors, setToErrors] = useState();
  const [fromErrors, setFromErrors] = useState();
  const [errors, setErrors] = useState();

  const onFocusChange = focusedInput => {
    if (focusedInput === "endDate") setCalendarFocus(focusedInput);
    else setShowCalendar(false);
  };

  const isInvalidTimeRange = (frmDate, tDate) => {
    return frmDate && tDate && !moment(frmDate).isSameOrBefore(tDate);
  };

  const handleTimeRange = (frmDate, tDate) => {
    const newErrors = cleanseObj({
      ...errors,
      invalidTimeRange: isInvalidTimeRange(frmDate, tDate)
    });
    setErrors(newErrors);
    onCalendarError(convertToArray(newErrors));
    onCalendarChange(frmDate, tDate);
  };


  const renderFromDate = () => (
    <div className="flex flex-1 items-center">
      <Text className="pr-2">From:</Text>
      <DateField
        parentDate={fromDate}
        selectLast={selected === "from"}
        next={() => setSelected("to")}
        parentSelect={() => setSelected("from")}
        selected={selected === "from"}
        onFocus={() => setCalendarFocus("startDate")}
        onDateChange={newFromDate => {
          setFromDate(newFromDate);
          handleTimeRange(newFromDate, toDate);
        }}
        onError={err => {
          setFromErrors(err);
          const newErrors = cleanseObj({ ...err, ...toErrors });
          onCalendarError(convertToArray(newErrors));
        }}
      />
    </div>
  );

  const renderToDate = () => (
    <div className="flex flex-1 items-center">
      <Text className="pr-2">To:</Text>
      <DateField
        parentDate={toDate}
        selectFirst={selected === "to"}
        previous={() => setSelected("from")}
        parentSelect={() => setSelected("to")}
        selected={selected === "to"}
        onFocus={() => setCalendarFocus("endDate")}
        onDateChange={newToDate => {
          setToDate(newToDate);
          handleTimeRange(fromDate, newToDate);
        }}
        errors={toErrors}
        onError={err => {
          setToErrors(err);
          const newErrors = cleanseObj({ ...err, ...fromErrors });
          onCalendarError(convertToArray(newErrors));
        }}
      />
    </div>
  );

  const renderDesktop = () => (
    <ClickAwayListener onClickAway={() => {}}>
      <div className="w-full flex relative">
        <InputContainer
          className="flex flex-1 md:mb-0"
          style={{ paddingTop: 0, paddingBottom: 0 }}
        >
          <div className="flex flex-1" onClick={() => setShowCalendar(true)}>
            {renderFromDate()}
          </div>
          <div className="flex flex-1" onClick={() => setShowCalendar(true)}>
            {renderToDate()}
          </div>
          <button
            type="button"
            onClick={() => {
              setCalendarFocus("startDate");
              setShowCalendar(!showCalendar);
            }}
          >
            <img src={calendarIcon} alt="set date range" style={{width: '1em'}} />
          </button>
        </InputContainer>
        {showCalendar && (
          <div className="absolute z-10" style={{ marginTop: 50 }}>
            <DayPickerRangeController
              startDate={fromDate}
              endDate={toDate}
              focusedInput={calendarFocus}
              onFocusChange={onFocusChange}
              onDatesChange={({ startDate, endDate }) => {
                setFromDate(startDate || fromDate);
                setToDate(endDate || toDate);
                handleTimeRange(startDate, endDate);
              }}
              initialVisibleMonth={() => moment().subtract(1, "M")}
              numberOfMonths={isBelow("md") ? 1 : 2}
              onOutsideClick={() => setShowCalendar(false)}
              showInputs={false}
            />
          </div>
        )}
      </div>
    </ClickAwayListener>
  );

  const renderMobile = () => (
    <div className="w-full mr-3 flex flex-col">
      <InputContainer
        className="flex flex-1 mb-2"
        style={{ paddingTop: 0, paddingBottom: 0 }}
      >
        {renderFromDate()}
      </InputContainer>
      <InputContainer
        className="flex flex-1 md:mb-0"
        style={{ paddingTop: 0, paddingBottom: 0 }}
      >
        {renderToDate()}
      </InputContainer>
    </div>
  );
  return isMobile ? renderMobile() : renderDesktop();
};

Calendar.defaultProps = {
  onCalendarError: () => {},
  onCalendarComplete: () => {}
};

export default Calendar;
